import { z } from 'zod';

import { DEFAULT_TIME_ZONE } from '@/shared/time-zones';

const SLUG = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const required = { error: 'organizations:validation.required' };

/** Form behind POST /platform/v1/organizations; the mapper in api/ turns blanks into omitted keys. */
export const provisionSchema = z
  .object({
    name: z.string().trim().min(2, required).max(120),
    slug: z.string().trim().min(1, required).max(120).regex(SLUG, {
      error: 'organizations:validation.slugFormat',
    }),
    billingEmail: z.email({ error: 'organizations:validation.email' }).or(z.literal('')),
    owner: z.object({
      name: z.string().trim().min(1, required).max(120),
      email: z.email({ error: 'organizations:validation.email' }),
      sendInvite: z.boolean(),
      password: z.string().max(200),
    }),
    store: z.object({
      name: z.string().trim().max(120),
      slug: z
        .string()
        .trim()
        .max(120)
        .refine((v) => v === '' || SLUG.test(v), {
          error: 'organizations:validation.slugFormat',
        }),
      defaultCurrency: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{3}$/, { error: 'organizations:validation.currency' }),
      countryCode: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}$/, { error: 'organizations:validation.country' }),
      timezone: z.string().min(1, required).max(64),
    }),
  })
  // The backend emails a set-password invitation when the password is left out.
  .superRefine((values, ctx) => {
    if (values.owner.sendInvite || values.owner.password.length >= 12) return;
    ctx.addIssue({
      code: 'custom',
      path: ['owner', 'password'],
      message: 'organizations:validation.passwordMin',
    });
  });

export type ProvisionInput = z.input<typeof provisionSchema>;
export type ProvisionOutput = z.output<typeof provisionSchema>;

export function emptyProvisionForm(): ProvisionInput {
  return {
    name: '',
    slug: '',
    billingEmail: '',
    owner: { name: '', email: '', sendInvite: true, password: '' },
    store: {
      name: '',
      slug: '',
      defaultCurrency: 'USD',
      countryCode: 'US',
      timezone: DEFAULT_TIME_ZONE,
    },
  };
}
